"use client"

import { useMemo, useState } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Badge } from "@/components/ui/badge"
import { AlertCircle, CheckCircle, Clock, Copy, Shield } from "lucide-react"
import { Button } from "@/components/ui/button"
import { toast } from "sonner"

interface JwtHeader {
  alg?: string
  typ?: string
  kid?: string
  [key: string]: unknown
}

interface JwtPayload {
  iss?: string
  sub?: string
  aud?: string | string[]
  exp?: number
  nbf?: number
  iat?: number
  jti?: string
  [key: string]: unknown
}

interface DecodedJwt {
  header: JwtHeader
  payload: JwtPayload
  signature: string
}

function base64UrlDecode(str: string): string {
  // Replace URL-safe characters
  let base64 = str.replace(/-/g, "+").replace(/_/g, "/")
  // Add padding if needed
  const padding = base64.length % 4
  if (padding) {
    base64 += "=".repeat(4 - padding)
  }
  try {
    return decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    )
  } catch {
    return atob(base64)
  }
}

function decodeJwt(token: string): DecodedJwt | null {
  try {
    const parts = token.trim().split(".")
    if (parts.length !== 3) {
      return null
    }

    const header = JSON.parse(base64UrlDecode(parts[0]))
    const payload = JSON.parse(base64UrlDecode(parts[1]))
    const signature = parts[2]

    return { header, payload, signature }
  } catch {
    return null
  }
}

function formatTimestamp(timestamp: number): string {
  const date = new Date(timestamp * 1000)
  return date.toLocaleString() + " (" + date.toISOString() + ")"
}

function getTokenStatus(payload: JwtPayload): { status: "valid" | "expired" | "not-yet-valid"; message: string } {
  const now = Math.floor(Date.now() / 1000)

  if (payload.exp && payload.exp < now) {
    const expiredAgo = now - payload.exp
    const hours = Math.floor(expiredAgo / 3600)
    const minutes = Math.floor((expiredAgo % 3600) / 60)
    return {
      status: "expired",
      message: `Expired ${hours > 0 ? hours + "h " : ""}${minutes}m ago`,
    }
  }

  if (payload.nbf && payload.nbf > now) {
    return {
      status: "not-yet-valid",
      message: `Not valid until ${formatTimestamp(payload.nbf)}`,
    }
  }

  if (payload.exp) {
    const expiresIn = payload.exp - now
    const hours = Math.floor(expiresIn / 3600)
    const minutes = Math.floor((expiresIn % 3600) / 60)
    return {
      status: "valid",
      message: `Expires in ${hours > 0 ? hours + "h " : ""}${minutes}m`,
    }
  }

  return { status: "valid", message: "No expiration set" }
}

export function JwtDecoder() {
  const [token, setToken] = useState("")
  const decoded = useMemo(() => (token.trim() ? decodeJwt(token) : null), [token])
  const error = token.trim() && !decoded
    ? "Invalid JWT format. A valid JWT has three parts separated by dots."
    : null

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    toast.success(`${label} copied to clipboard`)
  }

  const tokenStatus = decoded ? getTokenStatus(decoded.payload) : null

  const claimLabels: Record<string, string> = {
    iss: "Issuer",
    sub: "Subject",
    aud: "Audience",
    exp: "Expiration Time",
    nbf: "Not Before",
    iat: "Issued At",
    jti: "JWT ID",
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>JWT Decoder</CardTitle>
        <CardDescription>
          Decode and inspect JSON Web Tokens. All processing happens in your browser - your token is never sent to any server.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <Label htmlFor="jwt-input">Paste your JWT</Label>
          <Textarea
            id="jwt-input"
            placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiIxMjM0NTY3ODkwIiwibmFtZSI6IkpvaG4gRG9lIiwiaWF0IjoxNTE2MjM5MDIyfQ.SflKxwRJSMeKKF2QT4fwpMeJf36POk6yJV_adQssw5c"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            className="font-mono text-sm min-h-[100px]"
          />
        </div>

        {error && (
          <div className="flex items-center gap-2 text-destructive bg-destructive/10 p-3 rounded-lg">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {decoded && (
          <div className="space-y-4">
            {/* Token Status */}
            {tokenStatus && (
              <div
                className={`flex items-center gap-2 p-3 rounded-lg ${
                  tokenStatus.status === "valid"
                    ? "bg-green-50 dark:bg-green-950/20 text-green-700 dark:text-green-300"
                    : tokenStatus.status === "expired"
                      ? "bg-red-50 dark:bg-red-950/20 text-red-700 dark:text-red-300"
                      : "bg-yellow-50 dark:bg-yellow-950/20 text-yellow-700 dark:text-yellow-300"
                }`}
              >
                {tokenStatus.status === "valid" ? (
                  <CheckCircle className="h-4 w-4" />
                ) : tokenStatus.status === "expired" ? (
                  <AlertCircle className="h-4 w-4" />
                ) : (
                  <Clock className="h-4 w-4" />
                )}
                <span className="text-sm font-medium">{tokenStatus.message}</span>
              </div>
            )}

            {/* Header */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Shield className="h-4 w-4" />
                  Header
                  <Badge variant="secondary" className="ml-2">
                    {decoded.header.alg || "Unknown"}
                  </Badge>
                </Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(JSON.stringify(decoded.header, null, 2), "Header")}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <pre className="bg-muted p-3 rounded-lg overflow-x-auto text-sm font-mono">
                {JSON.stringify(decoded.header, null, 2)}
              </pre>
            </div>

            {/* Payload */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Payload</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyToClipboard(JSON.stringify(decoded.payload, null, 2), "Payload")}
                >
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
              <pre className="bg-muted p-3 rounded-lg overflow-x-auto text-sm font-mono">
                {JSON.stringify(decoded.payload, null, 2)}
              </pre>
            </div>

            {/* Decoded Claims */}
            <div className="space-y-2">
              <Label>Decoded Claims</Label>
              <div className="bg-muted rounded-lg divide-y">
                {Object.entries(decoded.payload).map(([key, value]) => {
                  const isTimestamp = ["exp", "nbf", "iat"].includes(key) && typeof value === "number"
                  return (
                    <div key={key} className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-4 p-3">
                      <div className="font-medium text-sm min-w-[120px]">
                        {claimLabels[key] || key}
                        <span className="text-muted-foreground ml-1">({key})</span>
                      </div>
                      <div className="text-sm font-mono break-all">
                        {isTimestamp ? (
                          <span>
                            {formatTimestamp(value as number)}
                          </span>
                        ) : typeof value === "object" ? (
                          JSON.stringify(value)
                        ) : (
                          String(value)
                        )}
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>

            {/* Signature */}
            <div className="space-y-2">
              <Label>Signature</Label>
              <div className="bg-muted p-3 rounded-lg">
                <code className="text-sm font-mono break-all text-muted-foreground">
                  {decoded.signature}
                </code>
                <p className="text-xs text-muted-foreground mt-2">
                  Note: This tool only decodes the JWT. It does not verify the signature.
                </p>
              </div>
            </div>
          </div>
        )}

        {!token && !error && (
          <div className="text-center py-8 text-muted-foreground">
            <p>Paste a JWT above to decode it</p>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
